import { useEffect } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";
import type { SocialPlatform } from "./SocialPlatformSheet";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: SocialPlatform | null;
  onTrySample: () => void;
}

const PLATFORM_META: Record<
  SocialPlatform,
  { label: string; icon: string; bg: string }
> = {
  tiktok: { label: "TikTok", icon: "ti-brand-tiktok", bg: "#000000" },
  instagram: {
    label: "Instagram",
    icon: "ti-brand-instagram",
    bg: "linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)",
  },
  pinterest: { label: "Pinterest", icon: "ti-brand-pinterest", bg: "#E60023" },
  facebook: { label: "Facebook", icon: "ti-brand-facebook", bg: "#1877F2" },
};

const SHARE_APPS: { id: string; label: string; icon: string; bg: string; iconColor?: string }[] = [
  { id: "messages", label: "Messages", icon: "ti-message", bg: "#34C759" },
  { id: "mail", label: "Mail", icon: "ti-mail", bg: "#007AFF" },
  // Like2Fit is rendered separately as a highlighted card.
  { id: "notes", label: "Notes", icon: "ti-note", bg: "#FFCC00" },
  { id: "whatsapp", label: "WhatsApp", icon: "ti-brand-whatsapp", bg: "#25D366" },
];

export default function SocialHowToSheet({ open, onOpenChange, platform, onTrySample }: Props) {
  useEffect(() => {
    if (open && platform) trackEvent("social_howto_viewed", { platform });
  }, [open, platform]);

  if (!platform) return null;
  const meta = PLATFORM_META[platform];

  const handleCta = () => {
    void triggerHaptic("selection");
    trackEvent("social_sample_tapped", { platform });
    onTrySample();
  };

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-[60]"
          style={{ background: "rgba(44,40,32,0.4)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-[61] flex flex-col"
          style={{
            background: "#FAECE7",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            paddingTop: 12,
            paddingLeft: 20,
            paddingRight: 20,
            paddingBottom: "calc(24px + var(--safe-bottom))",
            maxHeight: "92vh",
            overflowY: "auto",
            outline: "none",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 5,
              borderRadius: 999,
              background: "#D4CCC0",
              margin: "0 auto 16px",
            }}
          />

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 22 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: meta.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className={`ti ${meta.icon}`} style={{ fontSize: 18, color: "#FFFFFF" }} />
            </div>
            <DrawerPrimitive.Title
              style={{
                fontSize: 18,
                lineHeight: "24px",
                fontWeight: 500,
                color: "#2C2820",
                letterSpacing: "-0.3px",
                marginLeft: 12,
              }}
            >
              How to share to Like2Fit
            </DrawerPrimitive.Title>
          </div>
          <DrawerPrimitive.Description className="sr-only">
            Three steps to share an outfit from {meta.label} to Like2Fit.
          </DrawerPrimitive.Description>

          {/* Steps */}
          {[
            `Open ${meta.label} and find an outfit you love`,
            "Tap the Share icon",
            "Select Like2Fit from the share menu",
          ].map((text, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 14,
                marginBottom: i === 2 ? 14 : 18,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: "#C84B2F",
                  color: "#FFFCF8",
                  fontSize: 12,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </div>
              <div
                style={{
                  fontSize: 14,
                  lineHeight: "22px",
                  color: "#2C2820",
                  paddingTop: 1,
                }}
              >
                {text}
              </div>
            </div>
          ))}

          {/* iOS share sheet preview */}
          <div
            style={{
              background: "#F2F2F7",
              border: "0.5px solid #E8E2D9",
              borderRadius: 14,
              padding: "14px 12px",
              marginTop: 4,
            }}
          >
            {/* Top doc row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                paddingBottom: 10,
                borderBottom: "0.5px solid #D4D4D9",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 6,
                  background: "#D4D4D9",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "#1C1C1E" }}>
                  Smart-casual fit
                </div>
                <div style={{ fontSize: 11, color: "#6B6B70" }}>{meta.label} video</div>
              </div>
              <div style={{ fontSize: 13, color: "#007AFF", fontWeight: 500 }}>Done</div>
            </div>

            {/* App row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                paddingTop: 12,
                gap: 6,
              }}
            >
              {/* Slot 1 */}
              <ShareApp app={SHARE_APPS[0]} />
              {/* Slot 2 */}
              <ShareApp app={SHARE_APPS[1]} />
              {/* Slot 3 — Like2Fit highlighted */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  transform: "scale(1.06)",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 11,
                    background: "#E8E0D5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 0 2px #C84B2F",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#C84B2F" }}>L2F</span>
                </div>
                <div style={{ fontSize: 9, color: "#1C1C1E", fontWeight: 500 }}>Like2Fit</div>
              </div>
              {/* Slot 4 */}
              <ShareApp app={SHARE_APPS[2]} />
              {/* Slot 5 */}
              <ShareApp app={SHARE_APPS[3]} />
            </div>
          </div>

          <div
            style={{
              fontSize: 11,
              color: "#9B958E",
              fontStyle: "italic",
              textAlign: "center",
              marginTop: 8,
              marginBottom: 18,
            }}
          >
            Preview — Like2Fit appears here after install
          </div>

          {/* Divider */}
          <div
            style={{
              height: 0,
              borderTop: "0.5px solid #E8E2D9",
              margin: "0 -20px 18px",
            }}
          />

          {/* CTA */}
          <button
            type="button"
            onClick={handleCta}
            style={{
              width: "100%",
              background: "#C84B2F",
              color: "#FFFCF8",
              borderRadius: 14,
              padding: 16,
              border: "none",
              fontSize: 15,
              fontWeight: 500,
              textAlign: "center",
              cursor: "pointer",
              transition: "transform 120ms ease",
            }}
            onPointerDown={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.98)";
            }}
            onPointerUp={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
            }}
            onPointerLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
            }}
          >
            Try a sample to see how it works
          </button>
          <div
            style={{
              fontSize: 11,
              color: "#9B958E",
              textAlign: "center",
              marginTop: 12,
            }}
          >
            Or come back after sharing from {meta.label}
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

function ShareApp({ app }: { app: (typeof SHARE_APPS)[number] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          background: app.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className={`ti ${app.icon}`} style={{ fontSize: 22, color: "#FFFFFF" }} />
      </div>
      <div style={{ fontSize: 9, color: "#1C1C1E" }}>{app.label}</div>
    </div>
  );
}
